import { itx } from "../../../src/translationItx/itx";
import { jsPDF } from "jspdf";
import { loadPdfDocumentHeader } from "../../../src/pdf/PdfDocumentHeader";

export type C1_PdfTableOfContentsEntry = {
  title: string;
  contentPageIndex: number;
  level?: 1 | 2 | 3;
  pageLabel?: string;
};

type C1_PdfTableOfContentsOptions = {
  entries: C1_PdfTableOfContentsEntry[];
  projectName?: string;
  pageSize: "A3" | "A4";
  orientation: "landscape" | "portrait";
  /** Cover/documentation plus any general-note pages before the first chapter. */
  pagesBeforeContent?: number;
  globalPageNumbers?: boolean;
};

export type C1_PdfTableOfContentsResult = {
  blob: Blob;
  pageCount: number;
  links: Array<{
    tableOfContentsPageIndex: number;
    contentPageIndex: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
};



export async function createC1PdfTableOfContentsBlob(
  options: C1_PdfTableOfContentsOptions,
): Promise<C1_PdfTableOfContentsResult> {
  const format = options.pageSize.toLowerCase() as "a3" | "a4";
  const document = new jsPDF({ orientation: options.orientation, unit: "pt", format, compress: true });
  const header = await loadPdfDocumentHeader();
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const margin = 45;
  const contentTop = 105;
  const contentBottom = pageHeight - 58;
  const rowHeight = 20;
  const rowsPerPage = Math.max(1, Math.floor((contentBottom - contentTop) / rowHeight));
  const pageCount = Math.max(1, Math.ceil(options.entries.length / rowsPerPage));
  const firstContentPageNumber = 1 + (options.pagesBeforeContent ?? 2) + pageCount;
  const links: C1_PdfTableOfContentsResult["links"] = [];

  const drawPageFrame = (pageIndex: number) => {
    header.draw(document, { planType: itx("pageDocumentType.coverPage", "Inhaltsverzeichnis"), hierarchy: ["", ""] });
    document.setFont("helvetica", "bold");
    document.setFontSize(18);
    document.text(itx("pageDocumentType.coverPage", "Inhaltsverzeichnis"), margin, 92);
    document.setDrawColor("#aeb9bf");
    document.line(margin, pageHeight - 48, pageWidth - margin, pageHeight - 48);
    document.setTextColor("#586a75");
    document.setFont("helvetica", "normal");
    document.setFontSize(6.5);
    if (!options.globalPageNumbers) document.text(itx("c1.ui.Inhaltsverzeichnis_value1_value2", "Inhaltsverzeichnis {{value1}} / {{value2}}", { "{{value1}}": String(pageIndex + 1), "{{value2}}": String(pageCount) }), pageWidth - margin, pageHeight - 34, {
      align: "right",
    });
  };

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    if (pageIndex > 0) document.addPage(format, options.orientation);
    drawPageFrame(pageIndex);
    const entries = options.entries.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage);
    entries.forEach((entry, rowIndex) => {
      const y = contentTop + rowIndex * rowHeight;
      links.push({
        tableOfContentsPageIndex: pageIndex,
        contentPageIndex: entry.contentPageIndex,
        x: margin,
        y,
        width: pageWidth - margin * 2,
        height: rowHeight,
      });
      const pageNumber = firstContentPageNumber + entry.contentPageIndex;
      document.setFont("helvetica", "normal");
      document.setFontSize(9);
      document.setTextColor("#263b4a");
      const level = entry.level ?? 1;
      const title = entry.title.trim() || itx("c1.ui.Ohne_Bezeichnung", "Ohne Bezeichnung");
      document.setFont("helvetica", level === 1 ? "bold" : "normal");
      document.text(title, margin + (level - 1) * 18, y + 12, { maxWidth: pageWidth - margin * 2 - 63 });
      document.setDrawColor("#d8e0e5");
      document.setLineDashPattern([1, 2], 0);
      document.line(margin, y + 17, pageWidth - margin, y + 17);
      document.setLineDashPattern([], 0);
      document.setFillColor("#ffffff");
      document.rect(pageWidth - margin - 34, y + 3, 34, 15, "F");
      document.setFont("helvetica", "bold");
      document.text(entry.pageLabel ?? String(pageNumber), pageWidth - margin, y + 12, { align: "right" });
    });
  }

  return { blob: document.output("blob"), pageCount, links };
}
